/**
 * Where the identity provider's pages open. On the web the page itself goes there and comes back
 * with the code in the address. In the apps the system browser opens over the app and the redirect
 * to the app's own URL scheme brings the code back (appUrlOpen).
 */
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';

import { isNative } from './env';

/** The app's URL scheme (terrella, terrella.test); empty on the web. */
export const appScheme: string = import.meta.env.VITE_APP_SCHEME ?? '';

/** Where the identity provider sends the browser back to. */
export function redirectUri(): string {
  if (isNative && appScheme) return `${appScheme}://oauthcallback`;
  return `${location.origin}/`;
}

/**
 * Opens `url` for signing in and resolves with the redirect URL that came back (native), or never
 * resolves on the web, where the whole page leaves and returns through `callbackUrl()` at start.
 */
export async function openAuth(url: string): Promise<string | null> {
  if (!isNative) {
    location.assign(url);
    return new Promise(() => undefined);
  }
  return new Promise((resolve) => {
    let done = false;
    const finish = (value: string | null): void => {
      if (done) return;
      done = true;
      void Browser.close().catch(() => undefined);
      void opened.then((h) => h.remove());
      void closed.then((h) => h.remove());
      resolve(value);
    };
    const opened = App.addListener('appUrlOpen', ({ url: incoming }) => {
      if (incoming.startsWith(redirectUri())) finish(incoming);
    });
    // the person closed the browser without signing in
    const closed = Browser.addListener('browserFinished', () => {
      setTimeout(() => finish(null), 400);
    });
    void Browser.open({ url, presentationStyle: 'popover' }).catch(() => finish(null));
  });
}

/** On the web, the address the identity provider sent us back to, with the code; cleaned afterwards. */
export function callbackUrl(): string | null {
  if (isNative) return null;
  const url = new URL(location.href);
  if (!url.searchParams.has('code') && !url.searchParams.has('error')) return null;
  const clean = new URL(location.href);
  for (const key of ['code', 'state', 'session_state', 'iss', 'error', 'error_description'])
    clean.searchParams.delete(key);
  history.replaceState(null, '', clean.toString());
  return url.toString();
}
